import type { ChangeEvent, ChangeEventHandler, ReactNode } from 'react';
import { ErrorMessage, type FieldInputProps } from 'formik';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import InputErrorMessage from '../shared/InputErrorMessage';
import type { FormikValuesTouchedErrorsForm } from '../shared/formikTypes';
import { getFieldErrorState } from '../shared/formFieldUi';
import s from './RadioGroupInput.module.scss';
import InfoBubble from '../../InfoBubble/InfoBubble';

export interface RadioGroupOption {
  value: string;
  labelText?: string;
  labelMsg?: string;
  infoText?: string;
}

interface RadioGroupInputProps {
  field: FieldInputProps<string>;
  form: FormikValuesTouchedErrorsForm;
  options: RadioGroupOption[];
  captionMsg: string;
  radioButtonsWrapperClass?: string;
  renderCondition?: (values: Record<string, unknown>) => boolean;
  additionalChangeAction?: ChangeEventHandler<HTMLInputElement>;
  required?: boolean;
}

function renderRadioButton(
  field: FieldInputProps<string>,
  value: string,
  label: ReactNode,
  accessibleLabel: string,
  infoText: string | undefined,
  className: string,
  additionalChangeAction: ChangeEventHandler<HTMLInputElement> | undefined,
  required: boolean | undefined,
  isError: boolean,
  errorElementId: string,
  ariaDescribedBy: string | undefined,
  intl: IntlShape
) {
  const id = `${field.name}_${value}`;
  return (
    <div className={className} key={id}>
      <input
        type="radio"
        id={id}
        name={field.name}
        value={value}
        onChange={
          additionalChangeAction
            ? (event: ChangeEvent<HTMLInputElement>) => {
                additionalChangeAction(event);
                field.onChange(event);
              }
            : field.onChange
        }
        checked={field.value === value}
        required={required}
        aria-label={accessibleLabel}
        aria-describedby={ariaDescribedBy}
      />
      <div className={s.checkmark} />
      <label htmlFor={id} className={s.label}>
        {label}
        <span className={s.invisible}>{infoText}</span>
      </label>
      {infoText && (
        <div className={s.infoBubble}>
          <InfoBubble
            iconAltText={`${intl.formatMessage({
              id: 'radioGroupInput.infoBubble',
            })} ${accessibleLabel}`}
          >
            {infoText}
          </InfoBubble>
        </div>
      )}
    </div>
  );
}

function RadioGroupInput({
  field,
  options,
  captionMsg,
  radioButtonsWrapperClass,
  form,
  renderCondition,
  additionalChangeAction,
  required = false,
}: RadioGroupInputProps) {
  const intl = useIntl();

  if (renderCondition && !renderCondition(form.values)) {
    return null;
  }

  const { isError, errorElementId, ariaDescribedBy } = getFieldErrorState(
    form,
    field.name
  );

  const buttons = options.map((option) => {
    const { value, labelMsg, labelText, infoText } = option;
    const accessibleLabel =
      labelText ??
      (labelMsg != null ? intl.formatMessage({ id: labelMsg }) : '');
    const label =
      labelMsg != null ? <FormattedMessage id={labelMsg} /> : (labelText ?? '');
    return renderRadioButton(
      field,
      value,
      label,
      accessibleLabel,
      infoText,
      s.radioButton,
      additionalChangeAction,
      required,
      isError,
      errorElementId,
      ariaDescribedBy,
      intl
    );
  });

  return (
    <div className={s.radioGroupWrapper}>
      <fieldset tabIndex={-1} aria-invalid={isError} id={field.name}>
        <legend className={s.groupLabel}>
          <FormattedMessage id={captionMsg} />
          {required && '*'}
        </legend>
        <div className={radioButtonsWrapperClass}>{buttons}</div>
        <div id={errorElementId}>
          <ErrorMessage name={field.name} component={InputErrorMessage} />
        </div>
      </fieldset>
    </div>
  );
}

export default RadioGroupInput;
